'use client'

import {
  CornerDownRight,
  Maximize2,
  Play,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { useRef, useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

export type MediaWorkflowIdleMedia = {
  src: string
  kind: 'image' | 'video'
  /** First frame, so a dozen clips on one page cost nothing until played. */
  poster?: string
  /** Names the side, e.g. "Original ad · 9:16" / "Japanese cut". */
  label: string
  /**
   * This side's own shape when it differs from the sample's. An image-to-video
   * tool pairs a 3:4 photo with a 9:16 ad; forcing both into one aspect leaves
   * the photo adrift in a box cut for the video.
   */
  aspect?: string
}

type Side = 'before' | 'after'

export type MediaWorkflowIdleSample = {
  /** Short headline, e.g. "Beauty UGC · EN → JA". */
  label: string
  /** One line on what the visitor is looking at. */
  body: string
  /** Tailwind aspect class for each half; vertical ads want `aspect-[9/16]`. */
  aspect?: string
  /**
   * Absent on prompt-in tools. A text-to-video page has no source asset, so
   * the cell renders alone and the arrow is dropped — same rule as
   * `MediaWorkflowSamplePair`.
   */
  before?: MediaWorkflowIdleMedia
  after: MediaWorkflowIdleMedia
}

/**
 * What the tool makes, shown in the studio's result column before the visitor
 * has run anything.
 *
 * The column is otherwise a dashed "Your result appears here" box, which asks a
 * first-time visitor to imagine the output on the one screen that has to earn
 * the click.
 *
 * Both sides are shown, not just the output. On an edit tool the output alone
 * is unreadable as evidence — a Japanese ad proves nothing unless you can see
 * the English one it came from and check that the product, layout and presenter
 * survived. And for a translator the proof is specifically audible, so each
 * side carries its own sound toggle: press the left one to hear the original
 * read, the right one to hear the market cut.
 */
export function MediaWorkflowIdleSamples({
  samples,
  note,
}: {
  samples: MediaWorkflowIdleSample[]
  note: string
}) {
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({})
  const [soundOn, setSoundOn] = useState<string | null>(null)
  // Which half of which sample is open full-size. The column thumbnails are
  // deliberately small — they sit beside a form — so the only way to actually
  // read a translated ad here is to open it.
  const [zoom, setZoom] = useState<{ index: number; side: Side } | null>(null)

  const openZoom = (index: number, side: Side) => {
    // Stop every card before the dialog mounts its own player. These autoplay
    // on hover and one of them may have been unmuted, so leaving them running
    // behind the dialog is two copies of the same clip out of sync.
    for (const video of Object.values(videoRefs.current)) {
      video?.pause()
    }
    setSoundOn(null)
    setZoom({ index, side })
  }

  const keyOf = (i: number, side: 'before' | 'after') => `${i}:${side}`
  // Narrowed once here rather than at each of the six uses below: `before` is
  // optional now, so `samples[i][side]` is no longer known to exist.
  const zoomed = zoom ? samples[zoom.index][zoom.side] : null

  const playMedia = (key: string) => {
    void videoRefs.current[key]?.play().catch(() => {})
  }

  const pauseMedia = (key: string) => {
    const video = videoRefs.current[key]
    if (!video) return
    // The hover target is the media viewport itself. Leaving it is an
    // unconditional stop, including after the visitor has enabled sound.
    // Muting synchronously prevents a frame of audio while React commits the
    // matching button state.
    video.pause()
    video.currentTime = 0
    video.muted = true
    setSoundOn(current => (current === key ? null : current))
  }

  const toggleSound = (key: string) => {
    const next = soundOn === key ? null : key
    setSoundOn(next)
    for (const [entryKey, video] of Object.entries(videoRefs.current)) {
      if (!video) continue
      video.muted = entryKey !== next
      if (entryKey === next) void video.play().catch(() => {})
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs">{note}</p>
      {samples.map((sample, i) => (
        <div
          key={sample.label}
          className="group border-border bg-card overflow-hidden rounded-xl border"
        >
          <div
            className={cn(
              'grid items-center gap-1.5 p-2',
              sample.before ? 'grid-cols-[1fr_auto_1fr]' : '',
            )}
          >
            {(sample.before
              ? (['before', 'after'] as const)
              : (['after'] as const)
            ).map((side, index) => {
              const media = sample[side]!
              const key = keyOf(i, side)
              return (
                <div key={side} className="contents">
                  {index === 1 ? (
                    <CornerDownRight className="size-3.5 shrink-0 text-orange-500" />
                  ) : null}
                  <figure className="min-w-0">
                    {/* Capped rather than free-running: a 9:16 ad at column
                        width would own the fold on its own, and a column whose
                        height follows the active mode's shape makes the card
                        jump on every tab switch. `object-contain` letterboxes
                        inside the cap instead — on the CARD's own ground, not
                        black. This page's opening claim is that it does not
                        letterbox, and filling the leftover with black turned
                        the strip meant to prove that into a wall of black
                        bars; the bars were never in the media.

                        When a side declares its own aspect, the box switches
                        from column-width to a fixed height and takes its width
                        from that aspect, so the rounded frame hugs the media
                        and the two halves stay level — instead of a 3:4 photo
                        adrift in a box cut for a 9:16 ad. */}
                    <div
                      className={cn(
                        'group/media bg-muted relative overflow-hidden rounded-lg',
                        media.aspect
                          ? `mx-auto h-56 max-w-full ${media.aspect}`
                          : `max-h-56 ${sample.aspect ?? 'aspect-[4/5]'}`,
                      )}
                      onMouseEnter={() => {
                        if (media.kind === 'video') playMedia(key)
                      }}
                      onMouseLeave={() => {
                        if (media.kind === 'video') pauseMedia(key)
                      }}
                    >
                      {media.kind === 'video' ? (
                        <>
                          <video
                            ref={el => {
                              videoRefs.current[key] = el
                            }}
                            src={media.src}
                            poster={media.poster}
                            muted
                            loop
                            playsInline
                            preload={media.poster ? 'none' : 'metadata'}
                            className="h-full w-full object-contain"
                          />
                          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/15 transition-opacity group-hover/media:opacity-0">
                            <Play className="size-4 text-white/85" />
                          </div>
                          <button
                            type="button"
                            aria-label={
                              soundOn === key
                                ? `Mute ${media.label}`
                                : `Hear ${media.label}`
                            }
                            className="absolute right-1.5 bottom-1.5 rounded-full bg-black/60 p-1 text-white/90 transition-colors hover:bg-black/80"
                            onClick={() => toggleSound(key)}
                          >
                            {soundOn === key ? (
                              <Volume2 className="size-3" />
                            ) : (
                              <VolumeX className="size-3" />
                            )}
                          </button>
                        </>
                      ) : (
                        <img
                          src={media.src}
                          alt={media.label}
                          loading="lazy"
                          onClick={() => openZoom(i, side)}
                          className="h-full w-full cursor-zoom-in object-contain"
                        />
                      )}
                      <button
                        type="button"
                        aria-label={`View ${media.label} full size`}
                        onClick={() => openZoom(i, side)}
                        className="absolute top-1.5 left-1.5 rounded-full bg-black/60 p-1 text-white/90 opacity-0 transition-opacity group-hover/media:opacity-100 focus-visible:opacity-100"
                      >
                        <Maximize2 className="size-3" />
                      </button>
                    </div>
                    <figcaption className="text-muted-foreground mt-1 truncate text-center text-[10px] font-medium">
                      {media.label}
                    </figcaption>
                  </figure>
                </div>
              )
            })}
          </div>
          <div className="px-3 pt-1 pb-3">
            <h3 className="text-foreground text-xs font-semibold">
              {sample.label}
            </h3>
            <p className="text-muted-foreground mt-1 text-[11px] leading-4">
              {sample.body}
            </p>
          </div>
        </div>
      ))}

      <Dialog
        open={zoom !== null}
        onOpenChange={open => {
          if (!open) setZoom(null)
        }}
      >
        <DialogContent className="max-h-[92dvh] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] overflow-y-auto border-white/10 bg-stone-950 p-4 text-white sm:max-w-4xl sm:p-6">
          {zoom ? (
            <>
              <DialogTitle className="pr-8 font-sans text-xl leading-tight">
                {samples[zoom.index].label}
              </DialogTitle>
              <div
                className={cn(
                  'inline-flex gap-1 rounded-full border border-white/10 bg-white/5 p-1 text-xs font-semibold',
                  samples[zoom.index].before ? '' : 'hidden',
                )}
              >
                {(['before', 'after'] as const).map(side => (
                  <button
                    key={side}
                    type="button"
                    onClick={() => setZoom({ index: zoom.index, side })}
                    className={cn(
                      'rounded-full px-3 py-1.5 transition-colors',
                      zoom.side === side
                        ? 'bg-orange-500 text-stone-950'
                        : 'text-white/50 hover:bg-white/5 hover:text-white',
                    )}
                  >
                    {samples[zoom.index][side]?.label}
                  </button>
                ))}
              </div>
              <div className="flex max-h-[65dvh] items-center justify-center overflow-hidden rounded-xl bg-black/40">
                {zoomed?.kind === 'video' ? (
                  <video
                    key={zoomed.src}
                    src={zoomed.src}
                    poster={zoomed.poster}
                    controls
                    autoPlay
                    playsInline
                    className="max-h-[65dvh] w-auto max-w-full object-contain"
                  />
                ) : (
                  <img
                    src={zoomed?.src}
                    alt={zoomed?.label}
                    className="max-h-[65dvh] w-auto max-w-full object-contain"
                  />
                )}
              </div>
              <DialogDescription className="text-sm leading-6 text-white/50">
                {samples[zoom.index].body}
              </DialogDescription>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}
